import { Alert, Button, Card, EmptyState, formatDateTime } from '@catering/ui';
import { useMutation } from '@tanstack/react-query';
import { Download, FileSignature } from 'lucide-react';
import type { JSX } from 'react';

import { requestSignedScanDownloadUrl, type DeliveryNote } from '../../../delivery-notes/api';

export interface DeliveryNoteSignatureTabProps {
  note: DeliveryNote;
}

/**
 * RN-034: `needsReview: true` when `signedAt`/`serverReceivedAt` diverge
 * beyond the tolerance the API already validated — this tab only surfaces
 * the flag, it never recomputes it. `design.md` §9.2's "Revisión manual
 * requerida" indicator sits next to the signature data, not hidden in a
 * tooltip: it needs to be visible on a first glance.
 */
export function DeliveryNoteSignatureTab({ note }: DeliveryNoteSignatureTabProps): JSX.Element {
  const downloadScanMutation = useMutation({
    mutationFn: () => requestSignedScanDownloadUrl(note.id),
    onSuccess: (result) => {
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  if (note.status === 'VOID') {
    return (
      <Card>
        <EmptyState icon={FileSignature} title="El remito está anulado: no admite firma." />
      </Card>
    );
  }

  if (note.signature === null) {
    return (
      <Card>
        <EmptyState icon={FileSignature} title="Todavía no se cargó la firma de este remito." />
      </Card>
    );
  }

  const { signature } = note;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        {downloadScanMutation.isError ? (
          <Alert tone="danger">No pudimos descargar el remito firmado.</Alert>
        ) : null}
        {signature.needsReview ? (
          <Alert tone="warning" title="Revisión manual requerida">
            La firma se hizo en un horario muy distinto al de su carga (por ejemplo, firmó sin
            conexión). Verificá los datos antes de continuar.
          </Alert>
        ) : null}

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Recibido por</dt>
            <dd className="text-sm text-slate-900">{signature.receiverName}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Documento</dt>
            <dd className="text-sm text-slate-900">{signature.receiverDocument ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Cargo</dt>
            <dd className="text-sm text-slate-900">{signature.receiverRole ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Fecha de firma</dt>
            <dd className="text-sm text-slate-900">{formatDateTime(signature.signedAt)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Recibida en el sistema</dt>
            <dd className="text-sm text-slate-900">{formatDateTime(signature.serverReceivedAt)}</dd>
          </div>
        </dl>

        {note.status === 'SIGNED' ? (
          <div>
            <Button
              variant="secondary"
              icon={Download}
              loading={downloadScanMutation.isPending}
              onClick={() => downloadScanMutation.mutate()}
            >
              Descargar remito firmado
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
